import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { fetchContactsRequest } from '@/services/api/contact';
import { processAPIResponseNow } from '@/utils/request';
import { ContactModel } from '@/typings/models/Contact';
import { extractContactList, findDuplicateContact, isContactOfCustomer } from '@/utils/contacts';

const LOOKUP_DEBOUNCE_MS = 400;
/** `GET /contacts` ignores shorter search terms. */
const MIN_TERM_LENGTH = 3;

export type PartnerDuplicate = ReturnType<typeof findDuplicateContact>;

/**
 * A contact of the partner, not linked to this customer, that already uses this email or phone.
 * `GET /contacts` searches by prefix, so the exact match is picked client-side.
 */
export const usePartnerDuplicateContact = (
  { email, phone }: { email?: string | null; phone?: string | null },
  excludeCustomerId?: string,
): PartnerDuplicate => {
  const [duplicate, setDuplicate] = useState<PartnerDuplicate>(undefined);
  const term = (email || '').trim() || (phone || '').trim();

  const lookup = useMemo(
    () =>
      debounce((search: string, wanted: { email?: string | null; phone?: string | null }) => {
        fetchContactsRequest({ query: { search, page_size: 10, skip_total_size: 1 } }).then((response) =>
          processAPIResponseNow(response, {
            onSuccess: (body: unknown) => {
              const candidates = extractContactList(body).items.filter(
                (contact: ContactModel) => !isContactOfCustomer(contact, excludeCustomerId),
              );
              setDuplicate(findDuplicateContact(candidates, wanted));
            },
            onError: () => setDuplicate(undefined),
          }),
        );
      }, LOOKUP_DEBOUNCE_MS),
    [excludeCustomerId],
  );

  useEffect(() => {
    if (term.length < MIN_TERM_LENGTH) {
      lookup.cancel();
      setDuplicate(undefined);
      return;
    }
    lookup(term, { email, phone });
  }, [term, email, phone, lookup]);

  useEffect(() => () => lookup.cancel(), [lookup]);

  return duplicate;
};
